import { Capacitor } from '@capacitor/core';
import { buildApiUrl, getPrimaryBaseUrl, getSecondaryBaseUrl } from '../config/apiConfig';
import { resolveGoogleMapsApiKey } from '../config/resolveGoogleMapsApiKey';

interface Config {
  API_BASE_URL: string;
  API_SECONDARY_URL: string;
  GOOGLE_MAPS_API_KEY?: string;
  IS_MOBILE: boolean;
  IS_NATIVE: boolean;
}

class ConfigService {
  private config: Config;

  constructor() {
    this.config = this.initializeConfig();
  }

  private initializeConfig(): Config {
    const isNative = Capacitor.isNativePlatform();
    const isMobile = isNative || window.innerWidth < 768;

    let apiBaseUrl: string;
    let apiSecondaryUrl: string;

    if (isNative) {
      // App mobile native - utilise la logique unifiée
      apiBaseUrl = getPrimaryBaseUrl();
      apiSecondaryUrl = getSecondaryBaseUrl();
    } else {
      // Web - utilise les variables d'environnement via apiConfig
      apiBaseUrl = getPrimaryBaseUrl();
      apiSecondaryUrl = getSecondaryBaseUrl();
    }

    return {
      API_BASE_URL: apiBaseUrl,
      API_SECONDARY_URL: apiSecondaryUrl,
      GOOGLE_MAPS_API_KEY: resolveGoogleMapsApiKey(),
      IS_MOBILE: isMobile,
      IS_NATIVE: isNative
    };
  }

  public getConfig(): Config {
    return this.config;
  }

  public getApiUrl(): string {
    return this.config.API_BASE_URL;
  }

  public isMobile(): boolean {
    return this.config.IS_MOBILE;
  }

  public isNative(): boolean {
    return this.config.IS_NATIVE;
  }

  /** En-tête pour obtenir un JWT longue durée côté API (app Capacitor uniquement). */
  public getNativeClientHeaders(): Record<string, string> | undefined {
    if (!this.isNative()) return undefined;
    return { 'X-Rintintin-Client': 'native' };
  }

  /**
   * Retourne l'URL complète pour un endpoint donné en utilisant la logique de routage unifiée.
   */
  public getFullApiUrl(endpoint: string): string {
    return buildApiUrl(endpoint);
  }
}

// Instance singleton
export const configService = new ConfigService();

// Export des valeurs pour compatibilité
export const API_BASE_URL = configService.getApiUrl();
export const IS_MOBILE = configService.isMobile();
export const IS_NATIVE = configService.isNative();
